/** Store search returns this many hits per page. The API page size stays 100. */
export const STORE_PAGE_SIZE = 100;

export type ProductResultSummaryInput = {
    page: number;
    total: number;
    visibleCount: number;
    pageSize?: number;
};

function wholeCount(value: number): number {
    if (!Number.isFinite(value) || value <= 0) {
        return 0;
    }

    return Math.floor(value);
}

function productNoun(total: number): string {
    return total === 1 ? 'product' : 'products';
}

/**
 * Range for one accepted response. `page` is the page that produced `visibleCount`,
 * not a page the user has only requested. An empty hit list keeps the server total
 * instead of inventing a client-side count.
 */
export function formatProductResultSummary({page, total, visibleCount, pageSize = STORE_PAGE_SIZE}: ProductResultSummaryInput): string {
    const safeTotal = wholeCount(total);

    if (safeTotal === 0) {
        return '0 products';
    }

    const safeVisible = wholeCount(visibleCount);
    const safePageSize = wholeCount(pageSize) || STORE_PAGE_SIZE;
    const safePage = wholeCount(page) || 1;
    const noun = productNoun(safeTotal);

    if (safeVisible === 0) {
        return `0 of ${safeTotal} ${noun}`;
    }

    const start = (safePage - 1) * safePageSize + 1;
    const end = Math.min(safeTotal, start + safeVisible - 1);

    if (end < start) {
        return `0 of ${safeTotal} ${noun}`;
    }

    if (start === end) {
        return `${start} of ${safeTotal} ${noun}`;
    }

    return `${start}\u2013${end} of ${safeTotal} ${noun}`;
}

export function productResultPageCount(total: number, pageSize = STORE_PAGE_SIZE): number {
    const safeTotal = wholeCount(total);
    const safePageSize = wholeCount(pageSize) || STORE_PAGE_SIZE;

    if (safeTotal === 0) {
        return 0;
    }

    return Math.ceil(safeTotal / safePageSize);
}

/** Pagination stays hidden on an empty or single page, matching the previous bottom pager. */
export function shouldShowProductPagination(total: number, pageSize = STORE_PAGE_SIZE): boolean {
    return wholeCount(total) > (wholeCount(pageSize) || STORE_PAGE_SIZE);
}

/** react-paginate reports a 0-based index. The same page must not start another request. */
export function isNewStorePageSelection(currentPage: number, selectedIndex: number): boolean {
    if (!Number.isInteger(selectedIndex) || selectedIndex < 0) {
        return false;
    }

    return selectedIndex + 1 !== currentPage;
}

/**
 * A pager click fetches when it is a real page other than the one on screen.
 * The page on screen is the accepted page. Repeating the page that is already loading does not start another fetch.
 * After a failure, `requestedPage` is still the page that failed, so choosing it again retries that page.
 */
export function shouldRequestStorePage(selectedIndex: number, requestedPage: number, acceptedPage: number, loading: boolean): boolean {
    if (!isNewStorePageSelection(acceptedPage, selectedIndex)) {
        return false;
    }

    return !(loading && selectedIndex + 1 === requestedPage);
}
